(function () {
  'use strict';

  var CONFIG_ID = 'fbp-customer-profiles-v1-config';
  var TOKEN_KEY = 'fbp:customerProfiles:v1:visitorToken';
  var EXPIRES_KEY = 'fbp:customerProfiles:v1:visitorExpiresAt';
  var CLIENT_ID_KEY = 'fbp:customerProfiles:v1:clientId';
  var sessionIdentity = null;
  var sessionClientId = '';
  var VISITOR_COOKIE_KEY = 'fbp_customer_profiles_v1_visitor';

  function readConfig() {
    var element = document.getElementById(CONFIG_ID);
    if (!element) return { enabled: false };
    try {
      return JSON.parse(element.textContent || '{}');
    } catch (error) {
      return { enabled: false, parseError: true };
    }
  }

  function emit(name, detail) {
    document.dispatchEvent(new CustomEvent('fbp:customer-profiles:' + name, {
      bubbles: true,
      detail: detail || {}
    }));
  }

  function normalizePath(path) {
    var value = String(path || '/apps/customer-profiles-v1').trim();
    return value.charAt(0) === '/' ? value.replace(/\/+$/, '') : '/' + value.replace(/\/+$/, '');
  }

  function getStoredToken() {
    try {
      var token = window.localStorage.getItem(TOKEN_KEY);
      var expiresAt = window.localStorage.getItem(EXPIRES_KEY);
      if (!token || !expiresAt || Date.parse(expiresAt) <= Date.now()) return '';
      writeVisitorCookie(token, tokenCookieMaxAge({ expiresAt: expiresAt }));
      return token;
    } catch (error) {
      return sessionIdentity && Date.parse(sessionIdentity.expiresAt) > Date.now() ? sessionIdentity.visitorToken : '';
    }
  }

  function clearStoredToken() {
    sessionIdentity = null;
    try {
      window.localStorage.removeItem(TOKEN_KEY);
      window.localStorage.removeItem(EXPIRES_KEY);
    } catch (error) {}
    writeVisitorCookie('', 0);
  }

  function sharedCookieDomain() {
    var host = String(window.location.hostname || '').toLowerCase();
    return /(^|\.)furrybodypillows\.com$/.test(host) ? '.furrybodypillows.com' : '';
  }

  function writeVisitorCookie(token, maxAgeSeconds) {
    try {
      var parts = [
        VISITOR_COOKIE_KEY + '=' + encodeURIComponent(token || ''),
        'Path=/',
        'SameSite=Lax',
        'Max-Age=' + Math.max(0, Number(maxAgeSeconds || 0) || 0)
      ];
      var domain = sharedCookieDomain();
      if (domain) parts.push('Domain=' + domain);
      if (window.location.protocol === 'https:') parts.push('Secure');
      document.cookie = parts.join('; ');
    } catch (error) {}
  }

  function tokenCookieMaxAge(identity) {
    var expiresAt = Date.parse(identity && identity.expiresAt ? identity.expiresAt : '');
    if (!Number.isFinite(expiresAt)) return 365 * 24 * 60 * 60;
    return Math.max(0, Math.floor((expiresAt - Date.now()) / 1000));
  }

  function randomClientId() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return window.crypto.randomUUID();
    }
    return [
      Date.now().toString(36),
      Math.random().toString(36).slice(2),
      Math.random().toString(36).slice(2)
    ].join('-');
  }

  function getClientId() {
    if (sessionClientId) return sessionClientId;
    try {
      var existing = window.localStorage.getItem(CLIENT_ID_KEY);
      if (existing) return existing;
      var created = randomClientId();
      sessionClientId = created;
      window.localStorage.setItem(CLIENT_ID_KEY, created);
      return created;
    } catch (error) {
      return '';
    }
  }

  function storeToken(identity) {
    if (!identity || !identity.visitorToken) return;
    sessionIdentity = identity;
    try {
      window.localStorage.setItem(TOKEN_KEY, identity.visitorToken);
      if (identity.expiresAt) window.localStorage.setItem(EXPIRES_KEY, identity.expiresAt);
    } catch (error) {}
    writeVisitorCookie(identity.visitorToken, tokenCookieMaxAge(identity));
  }

  function tokenWasRejected(result) {
    var error = result && result.error;
    return error === 'malformed_token' ||
      error === 'bad_signature' ||
      error === 'expired_token' ||
      error === 'bad_payload' ||
      error === 'unknown_visitor' ||
      error === 'missing_profile';
  }

  function createBridge(initialConfig) {
    var config = Object.assign({
      enabled: false,
      proxyPath: '/apps/customer-profiles-v1',
      source: 'shopify-theme'
    }, initialConfig || {});
    var proxyPath = normalizePath(config.proxyPath);
    var identityPromise = null;
    var profileStatusPromise = null;
    var favoriteStatusPromises = Object.create(null);

    function disabledResponse() {
      return Promise.resolve({ ok: false, error: 'bridge_disabled' });
    }

    function anonymousProfileStatus() {
      return { ok: true, anonymous: true, profile: { hasCustomerAccount: false, activeFavoriteCount: 0, emailSubscription: { active: false } } };
    }

    function favoriteStatusCacheKey(productHandle) {
      var configuredProduct = config.product || {};
      return String(productHandle || configuredProduct.handle || '');
    }

    function clearStatusCaches(productHandle) {
      profileStatusPromise = null;
      if (productHandle) {
        delete favoriteStatusPromises[favoriteStatusCacheKey(productHandle)];
      } else {
        favoriteStatusPromises = Object.create(null);
      }
    }

    function request(path, options) {
      if (!config.enabled) return disabledResponse();
      var headers = Object.assign({ 'content-type': 'application/json' }, (options && options.headers) || {});
      var token = getStoredToken();
      if (token) headers.authorization = 'Bearer ' + token;

      return fetch(proxyPath + path, Object.assign({
        credentials: 'same-origin',
        headers: headers
      }, options || {})).then(function (response) {
        return response.json().then(function (body) {
          if (!response.ok && body && !body.error) body.error = 'request_failed';
          if (!response.ok && tokenWasRejected(body)) clearStoredToken();
          return body;
        });
      }).catch(function (error) {
        return { ok: false, error: error && error.message ? error.message : 'network_error' };
      });
    }

    function favoriteRequest(path, options) {
      var controller = new AbortController();
      var timer = window.setTimeout(function () { controller.abort(); }, 12000);
      return request(path, Object.assign({}, options || {}, { signal: controller.signal, cache: 'no-store' }))
        .then(function (result) { window.clearTimeout(timer); return result; }, function (error) {
          window.clearTimeout(timer); return { ok: false, error: 'network_error' };
        });
    }

    function ensureIdentity(forceNew) {
      if (forceNew) {
        clearStoredToken();
        identityPromise = null;
        clearStatusCaches();
      }
      var token = getStoredToken();
      if (token) return Promise.resolve({ ok: true, visitorToken: token, existing: true });

      if (identityPromise) return identityPromise;

      identityPromise = favoriteRequest('/v1/identity/guest', {
        method: 'POST',
        body: JSON.stringify({
          source: config.source || 'shopify-theme',
          clientId: getClientId()
        })
      }).then(function (identity) {
        if (identity.ok) storeToken(identity);
        emit('identity', identity);
        identityPromise = null;
        return identity;
      }, function (error) {
        identityPromise = null;
        throw error;
      });
      return identityPromise;
    }

    function favoritePayload(product, action) {
      var selected = product || config.product || {};
      var metadata = {
        productHandle: selected.handle || selected.productHandle || '',
        productId: selected.id || selected.productId || '',
        productTitle: selected.title || selected.productTitle || '',
        productUrl: selected.url || selected.productUrl || '',
        featuredImageUrl: selected.featuredImageUrl || selected.imageUrl || selected.image || '',
        thumbnailImageUrl: selected.thumbnailImageUrl || selected.thumbUrl || selected.thumbnail || '',
        imageAlt: selected.imageAlt || selected.alt || selected.title || selected.productTitle || '',
        priceCents: selected.priceCents || selected.price || '',
        compareAtPriceCents: selected.compareAtPriceCents || selected.compareAtPrice || '',
        currency: selected.currency || '',
        artist: selected.artist || selected.productType || '',
        productType: selected.productType || selected.type || '',
        vendor: selected.vendor || ''
      };
      return {
        productHandle: selected.handle || selected.productHandle || '',
        productId: selected.id || selected.productId || '',
        productTitle: selected.title || selected.productTitle || '',
        productMetadata: metadata,
        action: action || 'favorite',
        source: config.source || 'shopify-theme',
        clientId: getClientId(),
        idempotencyKey: [
          'theme',
          selected.handle || selected.productHandle || 'unknown',
          action || 'favorite',
          Date.now()
        ].join(':')
      };
    }

    return {
      version: '0.1.0',
      isEnabled: function () {
        return Boolean(config.enabled);
      },
      isCustomerLoggedIn: function () {
        return Boolean(config.customerLoggedIn);
      },
      init: function (nextConfig) {
        config = Object.assign(config, nextConfig || {});
        proxyPath = normalizePath(config.proxyPath);
        clearStatusCaches();
        emit('ready', { enabled: Boolean(config.enabled) });
        return this;
      },
      getVisitorToken: function () {
        return ensureIdentity();
      },
      getLeaderboard: function (limit) {
        return request('/v1/design-week/leaderboard?limit=' + encodeURIComponent(limit || 5));
      },
      getDesignWeekState: function (limit) {
        return request('/v1/design-week/state?limit=' + encodeURIComponent(limit || 5));
      },
      getLatestDesignWeek: function () {
        return request('/v1/design-week/latest');
      },
      getProfileStatus: function () {
        if (!getStoredToken() && !config.customerLoggedIn) return Promise.resolve(anonymousProfileStatus());
        if (profileStatusPromise) return profileStatusPromise;

        profileStatusPromise = ensureIdentity().then(function () {
          return request('/v1/profile/status').then(function (result) {
            if (!tokenWasRejected(result)) return result;
            return anonymousProfileStatus();
          });
        }).then(function (result) {
          if (!result || !result.ok) profileStatusPromise = null;
          return result;
        }, function (error) {
          profileStatusPromise = null;
          throw error;
        });
        return profileStatusPromise;
      },
      getSupportTickets: function (limit) {
        return ensureIdentity().then(function () {
          var query = '?limit=' + encodeURIComponent(limit || 5);
          return request('/v1/support/tickets' + query).then(function (result) {
            if (!tokenWasRejected(result)) return result;
            return ensureIdentity(true).then(function () {
              var retryQuery = '?limit=' + encodeURIComponent(limit || 5);
              return request('/v1/support/tickets' + retryQuery);
            });
          });
        });
      },
      sendSupportMessage: function (input) {
        return ensureIdentity().then(function (identity) {
          if (!identity.ok) return identity;
          return request('/v1/support/messages', {
            method: 'POST',
            body: JSON.stringify(Object.assign({
              source: config.source || 'shopify-theme',
              clientId: getClientId()
            }, input || {}))
          }).then(function (result) {
            emit('support-message', result);
            return result;
          });
        });
      },
      getFavoriteStatus: function (productHandle, options) {
        var handle = favoriteStatusCacheKey(productHandle);
        if (options && options.refresh) delete favoriteStatusPromises[handle];
        if (favoriteStatusPromises[handle]) return favoriteStatusPromises[handle];

        favoriteStatusPromises[handle] = Promise.resolve().then(function () {
          var query = '?product_handle=' + encodeURIComponent(handle);
          return favoriteRequest('/v1/favorites/status' + query).then(function (result) {
            if (!tokenWasRejected(result)) return result;
            return Promise.resolve().then(function () {
              var retryQuery = '?product_handle=' + encodeURIComponent(handle);
              return favoriteRequest('/v1/favorites/status' + retryQuery);
            });
          });
        }).then(function (result) {
          if (!result || !result.ok) delete favoriteStatusPromises[handle];
          return result;
        }, function (error) {
          delete favoriteStatusPromises[handle];
          throw error;
        });
        return favoriteStatusPromises[handle];
      },
      setFavorite: function (product, active) {
        return ensureIdentity().then(function (identity) {
          if (!identity.ok) return identity;
          var action = active === false ? 'unfavorite' : 'favorite';
          var handle = favoriteStatusCacheKey(product && (product.handle || product.productHandle));
          return favoriteRequest('/v1/favorites/toggle', {
            method: 'POST',
            body: JSON.stringify(favoritePayload(product, action))
          }).then(function (result) {
            clearStatusCaches(handle);
            if (result && result.ok && handle) {
              favoriteStatusPromises[handle] = Promise.resolve(result);
            }
            emit('favorite', result);
            return result;
          });
        });
      },
      subscribeEmail: function (email, extra) {
        return ensureIdentity().then(function (identity) {
          if (!identity.ok) return identity;
          return request('/v1/email/subscribe', {
            method: 'POST',
            body: JSON.stringify(Object.assign({
              email: email,
              source: config.source || 'shopify-theme'
            }, extra || {}))
          }).then(function (result) {
            profileStatusPromise = null;
            emit('subscribe', result);
            return result;
          });
        });
      },
      linkCurrentCustomer: function (extra) {
        return ensureIdentity().then(function (identity) {
          if (!identity.ok) return identity;
          return request('/v1/profiles/link-shopify-customer', {
            method: 'POST',
            body: JSON.stringify(Object.assign({}, extra || {}))
          }).then(function (result) {
            profileStatusPromise = null;
            emit('profile-link', result);
            return result;
          });
        });
      }
    };
  }

  var bridge = createBridge(readConfig());
  window.FBPProfilesBridge = bridge;
  bridge.init();
})();
